/**
 * Streaks, adherence and habit insights. Pure: every rule reads the same
 * per-day summaries and returns a card or null, and each has a minimum-data
 * gate so a new user isn't told "you always overeat on weekends" after four days.
 */

import { addDays, daysBetween, mondayOf, weekday } from './days.ts'

export type DaySummary = {
  day: string
  kcal: number
  protein_g: number
  fibre_g: number | null
  sodium_mg: number | null
  entries: number
  valid: boolean
  target: { kcal: number; protein_g: number; fibre_g: number | null } | null
}

export type Insight = {
  id: string
  priority: number        // higher shows first
  tone: 'good' | 'info' | 'warn'
  title: string
  body: string
}

export function loggingStreak(days: Map<string, DaySummary>, today: string): number {
  let d = (days.get(today)?.entries ?? 0) > 0 ? today : addDays(today, -1)
  let n = 0
  while ((days.get(d)?.entries ?? 0) > 0) { n++; d = addDays(d, -1) }
  return n
}

export function weighInStreak(weighDays: Set<string>, today: string): number {
  let d = weighDays.has(today) ? today : addDays(today, -1)
  let n = 0
  while (weighDays.has(d)) { n++; d = addDays(d, -1) }
  return n
}

export function onTarget(s: DaySummary): boolean | null {
  if (!s.valid || !s.target) return null
  const kOk = Math.abs(s.kcal - s.target.kcal) <= 0.1 * s.target.kcal
  const pOk = s.target.protein_g <= 0 || s.protein_g >= 0.9 * s.target.protein_g
  return kOk && pOk
}

/** Share of valid days on target over the `n` days ending yesterday. */
export function adherence(days: Map<string, DaySummary>, today: string, n = 7): { pct: number | null; counted: number } {
  let hit = 0, counted = 0
  for (let i = 1; i <= n; i++) {
    const s = days.get(addDays(today, -i))
    const o = s ? onTarget(s) : null
    if (o === null) continue
    counted++
    if (o) hit++
  }
  return { pct: counted ? Math.round((100 * hit) / counted) : null, counted }
}

export type WeekAverage = { week: string; kcal: number; protein_g: number; days: number }

export function weeklyAverages(days: Map<string, DaySummary>, today: string, weeks = 8): WeekAverage[] {
  const out: WeekAverage[] = []
  const thisMonday = mondayOf(today)
  for (let w = weeks - 1; w >= 0; w--) {
    const start = addDays(thisMonday, -7 * w)
    let k = 0, p = 0, n = 0
    for (let i = 0; i < 7; i++) {
      const d = addDays(start, i)
      if (d > today) break
      const s = days.get(d)
      if (s?.valid) { k += s.kcal; p += s.protein_g; n++ }
    }
    if (n) out.push({ week: start, kcal: Math.round(k / n), protein_g: Math.round(p / n), days: n })
  }
  return out
}

function avg(xs: number[]) { return xs.reduce((a, b) => a + b, 0) / xs.length }

export function insights(opts: {
  days: Map<string, DaySummary>
  today: string
  weighDays: Set<string>
  lastWeighIn: string | null
  rate14: number | null           // kg/week over 14 days
  goalType: 'lose' | 'maintain' | 'gain' | null
  eNow: number | null
  e28: number | null
  sodiumLimit: number
}): Insight[] {
  const { days, today, weighDays, lastWeighIn, rate14, goalType, eNow, e28, sodiumLimit } = opts
  const out: Insight[] = []
  const recent = (n: number) => {
    const xs: DaySummary[] = []
    for (let i = 1; i <= n; i++) { const s = days.get(addDays(today, -i)); if (s) xs.push(s) }
    return xs
  }

  const streak = loggingStreak(days, today)
  if (streak >= 3) {
    out.push({ id: 'streak', priority: streak >= 7 ? 50 : 30, tone: 'good',
      title: `${streak}-day logging streak`,
      body: streak >= 14 ? 'Consistency is what makes the expenditure estimate sharp.' : 'Keep it going.' })
  }

  const adh = adherence(days, today, 7)
  if (adh.counted >= 4 && adh.pct !== null) {
    out.push({ id: 'adherence', priority: 40, tone: adh.pct >= 70 ? 'good' : 'info',
      title: `On target ${adh.pct}% of the last week`,
      body: 'On target means calories within 10% and protein at 90% or more.' })
  }

  // Weekend vs weekday over the last 4 weeks.
  const last28 = recent(28).filter((s) => s.valid)
  const weekend = last28.filter((s) => weekday(s.day) >= 4)
  const weekdays = last28.filter((s) => weekday(s.day) < 4)
  if (weekend.length >= 6 && weekdays.length >= 9) {
    const we = avg(weekend.map((s) => s.kcal)), wd = avg(weekdays.map((s) => s.kcal))
    const gap = (we - wd) / wd
    if (Math.abs(gap) > 0.15) {
      out.push({ id: 'weekend', priority: 60, tone: 'warn',
        title: `Weekends run ${gap > 0 ? '+' : '−'}${Math.abs(Math.round(we - wd))} kcal`,
        body: `Friday to Sunday averages ${Math.round(we)} kcal against ${Math.round(wd)} on weekdays over the last four weeks.` })
    }
  }

  const week = recent(7)
  const valid7 = week.filter((s) => s.valid)
  if (valid7.length >= 4) {
    const withTarget = valid7.filter((s) => s.target && s.target.protein_g > 0)
    if (withTarget.length >= 4) {
      const p = avg(withTarget.map((s) => s.protein_g))
      const t = avg(withTarget.map((s) => s.target!.protein_g))
      if (p < 0.8 * t) {
        out.push({ id: 'protein', priority: 55, tone: 'warn',
          title: 'Protein is running low',
          body: `Averaging ${Math.round(p)} g against a ${Math.round(t)} g target this week.` })
      }
    }
    const fib = valid7.filter((s) => s.fibre_g !== null && s.target?.fibre_g)
    if (fib.length >= 4) {
      const f = avg(fib.map((s) => s.fibre_g!)), t = avg(fib.map((s) => s.target!.fibre_g!))
      if (f < 0.7 * t) {
        out.push({ id: 'fibre', priority: 25, tone: 'info', title: 'Fibre is low',
          body: `About ${Math.round(f)} g a day against ${Math.round(t)} g. Beans, oats, fruit and vegetables all help.` })
      }
    }
    const sod = valid7.filter((s) => s.sodium_mg !== null)
    if (sod.length >= 4) {
      const n = avg(sod.map((s) => s.sodium_mg!))
      if (n > 1.3 * sodiumLimit) {
        out.push({ id: 'sodium', priority: 20, tone: 'info', title: 'Sodium is high',
          body: `About ${Math.round(n).toLocaleString('en-CA')} mg a day. Salt also holds water, which blurs the scale.` })
      }
    }
  }

  const unlogged = week.filter((s) => s.entries === 0).length + (7 - week.length)
  if (unlogged >= 2 && daysBetween(firstDay(days) ?? today, today) >= 7) {
    out.push({ id: 'gaps', priority: 45, tone: 'info',
      title: `${unlogged} unlogged days this week`,
      body: 'Unlogged and incomplete days are left out of your expenditure estimate, so it learns more slowly.' })
  }

  if (lastWeighIn && daysBetween(lastWeighIn, today) >= 4) {
    out.push({ id: 'weigh', priority: 35, tone: 'info',
      title: 'Time to weigh in',
      body: `Last weigh-in was ${daysBetween(lastWeighIn, today)} days ago. Regular weigh-ins sharpen the estimate.` })
  }

  if (rate14 !== null && goalType && goalType !== 'maintain' && weighDays.size >= 7) {
    const wrongWay = goalType === 'lose' ? rate14 > 0.05 : rate14 < -0.05
    if (wrongWay) {
      out.push({ id: 'trend', priority: 58, tone: 'warn',
        title: `Trend is moving ${rate14 > 0 ? 'up' : 'down'}`,
        body: `Over two weeks the trend is ${rate14 > 0 ? '+' : ''}${rate14.toFixed(2)} kg/week, against your goal to ${goalType}.` })
    }
  }

  if (eNow !== null && e28 !== null && Math.abs(eNow - e28) >= 100) {
    out.push({ id: 'expenditure', priority: 42, tone: 'info',
      title: `Expenditure ${eNow > e28 ? 'up' : 'down'} ${Math.round(Math.abs(eNow - e28))} kcal`,
      body: 'Compared with four weeks ago. Your next check-in will take it into account.' })
  }

  return out.sort((a, b) => b.priority - a.priority)
}

function firstDay(days: Map<string, DaySummary>): string | null {
  let first: string | null = null
  for (const [d, s] of days) if (s.entries > 0 && (!first || d < first)) first = d
  return first
}
